import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { ArrowUp, CalendarClock } from "lucide-react";
import MemoryChip from "../../MemoryChip/MemoryChip";
import PromptSkillSelector from "../../../Prompt/PromptSkillSelector";
import PromptStyleSelector from "../../../Prompt/PromptStyleSelector";
import { BlackCross, WhiteCross } from "../../../../assets";
import SchedulerCard from "../../../Scheduler/SchedulerCard";
import { FileStore } from "../../../../store/fileUploadStore";
import UserAuthStore from "../../../../store/userStore";
import AttachDropdown from "../../../Dropdowns/AttachDropdown";
import PromptModelSelector from "../../../Prompt/PromptModelSelector";
import PromptDeviceSelector from "../../../Prompt/PromptDeviceSelector";
import WebSearchToggle from "../../../Prompt/WebSearchToggle";
import FileUploadItem from "../../../Prompt/FileUploadItem";
import FileChip from "../../../shared/FileChip";
import { handleFileUpload } from "../../../../utils/fileUploadHandler";
import { ALLOWED_FILE_ACCEPT_STRING } from "../../../../utils/helpers";
import MarkdownRenderer, { MARKDOWN_REMARK_PLUGINS, MARKDOWN_REHYPE_PLUGINS } from "../../../Markdown/MarkdownRenderer";
import CodeBlockSaveContext from "../../../../context/CodeBlockSaveContext";
import EnhancedMessageContent from "../../../Universal/MessageContent/EnhancedMessageContent.tsx";
import MessageFooter from "../../../shared/Message/MessageFooter";
import { MermaidSvgRegistryProvider } from "../../../../context/MermaidSvgRegistryContext";
import useGenerateApiSignal from "../../../../store/useGenerateApiSignal";
import useStreamingStateStore from "../../../../store/useStreamingStateStore";
import { transformContent, renderTextWithTags, formatTimestamp, DENIAL_ERROR_MESSAGES } from "../../../../utils/helpers";
import { MdBlock, MdHourglassEmpty } from "react-icons/md";
import { useUserTags, useAgentTriggers, useListSchedulerJobs, useGetSkills } from "../../../../api/queryHooks";
import PromptStore, { resolveSelectedAnswerId } from "../../../../store/promptStore";
import { ShowResearch } from "./ShowResearch";
import CitationsPreview from "../../../Common/CitationsPreview";
import YouTubeVideosSection from "../../../Common/YouTubeVideosSection";
import ApprovalCard from "./ApprovalCard";
import ProviderConnectionCard from "./ProviderConnectionCard";
import { useSparkContext } from "../../../../context/SparkContext";
import { stopGenerationAndReconcile } from "../../../../api/stopReconciliation";
import QUERYKEYS from "../../../../api/queryKeys";
import { useQueryClient } from "@tanstack/react-query";
import { GenerationStatusIndicator, useGenerationStatus } from "@photon/chat-react";
import AnswerPreview from "@photon/chat-react/answer-preview";
import ExchangeToggleButton from "@photon/chat-react/exchange-toggle-button";
import PromptMetadata from "./PromptMetadata";
import { canManuallyCollapseAnswer, canRetryAnswer, isStatusForActiveAnswer, measurePromptElement, shouldClearGenerationSignal, shouldCollapseAnswer, shouldRenderAnswer, shouldShowMessageActions, shouldShowTerminalStatus } from "@photon/chat-core";
import { containsActivitySnapshot, mergeRunningToolActivity, selectActivityItems } from "../../../shared/Timeline/activityItemUtils";

const emptyToolCalls = [];

const OneMessage = React.memo(({
  index,
  chat,
  user,
  theme,
  regenFunc,
  regenerateIndex,
  setRegenerateIndex,
  editedContent,
  setEditedContent,
  editFunctionality,
  generateStatus,
  setGenerateStatus,
  deleteMessage,
  isLatest,
  pendingApprovals = {},
  onApprovalConfirm,
  onApprovalCancel,
  onApprovalExpire,
  compactAnswers = true,
  expandedExchangeIds,
  collapsedExchangeIds,
  onExchangeExpandedChange,
}) => {
  const { chat_id, space_id, space } = useSparkContext();
  const queryClient = useQueryClient();
  const messageRef = useRef(null);
  const promptRef = useRef(null);
  const exchangeTopControlRef = useRef(null);
  const exchangeFooterControlRef = useRef(null);
  const textareaRef = useRef(null);
  const generate_id = useGenerateApiSignal((state) => state.signals[chat_id]?.generate_id);
  const activeAnswerId = useGenerateApiSignal((state) => state.signals[chat_id]?.answer_id);
  const generationOwnerId = useGenerateApiSignal((state) => state.signals[chat_id]?.owner_id);
  const statusText = useStreamingStateStore((state) => state.activeStreams[chat.id]?.statusText);
  const hasContent = useStreamingStateStore((state) => state.activeStreams[chat.id]?.hasContent ?? false);
  const toolCalls = useStreamingStateStore((state) => state.activeStreams[chat.id]?.toolCalls) ?? emptyToolCalls;
  const streamingActivityItems = useStreamingStateStore((state) => state.activeStreams[chat.id]?.activityItems) ?? null;
  const connectionRequirement = useStreamingStateStore(
    (state) => state.connectionRequirements[chat.id] ?? null
  );
  const isActivelyStreaming = useStreamingStateStore((state) => !!state.activeStreams[chat.id]?.streaming);
  const isStopping = useStreamingStateStore((state) => state.stoppingMessageIds.has(chat.id));
  const isCompletedSnapshot = useStreamingStateStore((state) => {
    const s = state.activeStreams[chat.id];
    return s && !s.streaming && ((s.toolCalls?.length ?? 0) > 0 || (s.activityItems?.length ?? 0) > 0);
  });
  const clearCompletedSnapshot = useStreamingStateStore((state) => state.clearCompletedSnapshot);
  // Timer for skeleton state (no content yet — State 1, 5s threshold)
  const skeletonStartedAtRef = useRef(null);
  const [skeletonElapsed, setSkeletonElapsed] = useState(0);
  // Show skeleton immediately for the latest message whose answer exists (placeholder
  // created) but has no content and no terminal status — covers the refresh recovery
  // window before useRefreshRecovery's async fetch resolves and sets generate_id.
  // Uses inline answer lookup to avoid forward-reference of currentAnswer (declared later).
  const _recoveryAns = chat.answers?.[chat.answers.length - 1];

  // Auto-dismiss state for the "stuck" fallback. Declared before the status
  // derivation below because the derivation reads it.
  const [stuckDismissed, setStuckDismissed] = useState(false);

  // Centralized generation-status derivation (Phase 2 cutover — replaces the
  // former inline isEmptyAwaitingRecovery / isSkeletonState / isStuck). A
  // dev-only parallel-run confirmed these outputs matched the old inline logic
  // across real usage before this switch. `status` (monotonic) is returned too
  // but the render still consumes the booleans below; wider status adoption is
  // a later step.
  const { isSkeleton: isSkeletonState, isStuck, isEmptyAwaitingRecovery } = useGenerationStatus({
    answerId: _recoveryAns?.id,
    hasContent,
    isActivelyStreaming,
    isStopping,
    hasSignal: generate_id === chat.id,
    isRegenerating: regenerateIndex === index,
    elapsedSeconds: skeletonElapsed,
    stuckDismissed,
    generateStatus,
    isLatest,
    recoveryAnswerId: _recoveryAns?.id,
    recoveryAnswerContent: _recoveryAns?.content,
    recoveryAnswerStatus: _recoveryAns?.status,
    // The backend never sets a terminal-ish `status` on the empty answer while
    // an approval card is pending — without this, the recovery skeleton (and
    // eventually the Stop/Dismiss stuck fallback after 600s) renders stacked
    // behind the ApprovalCard while the user is just deciding, not stuck.
    recoveryAnswerAwaitingApproval: !!_recoveryAns?.awaiting_approval,
  });

  useEffect(() => {
    if (isSkeletonState) {
      if (!skeletonStartedAtRef.current) skeletonStartedAtRef.current = Date.now();
      const interval = setInterval(() => {
        setSkeletonElapsed(Math.floor((Date.now() - skeletonStartedAtRef.current) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    } else {
      skeletonStartedAtRef.current = null;
      setSkeletonElapsed(0);
    }
  }, [isSkeletonState]);

  // Shared by GenerationStatusIndicator's render gate.
  const hasRunningTool = toolCalls.some(tc => tc.status === 'running' && !tc.name?.startsWith('load_skill'));

  const [activityOpen, setActivityOpen] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const isScheduling = showScheduler;
  const orgId = UserAuthStore((state) => state.orgId);
  const userId = UserAuthStore((state) => state.user?.uid);
  // canStop: only the user who initiated generation can stop it. If owner_id is not
  // set (e.g. recovery path before GENERATION_STARTED arrives), default to allowing
  // stop so the owner is never locked out.
  const canStop = !generationOwnerId || generationOwnerId === userId;
  const { data: tagsData } = useUserTags(orgId, userId);
  const validTags = tagsData?.tags || [];
  const { data: triggers = [] } = useAgentTriggers(orgId);
  const { data: schedulerJobs = [] } = useListSchedulerJobs();
  const scheduledJob = chat.id ? schedulerJobs.find((j) => {
    if (j.enabled === false) return false;
    if (j.message_id) return j.message_id === chat.id;
    return j.spark_id === chat_id && j.prompt === (chat.content || "").trim();
  }) : null;
  const isScheduled = !!scheduledJob;

  const selectedAnswerId = PromptStore((state) => state.selectedAnswerByPrompt[chat.id]);
  const setSelectedAnswer = PromptStore((state) => state.setSelectedAnswer);
  const followLatestAnswer = PromptStore((state) => state.followLatestAnswer);
  const reconcileSelectedAnswer = PromptStore((state) => state.reconcileSelectedAnswer);
  const visibleAnswers = chat.answers?.filter((answer) => answer?.id && !answer.awaiting_approval) || [];
  const displayedAnswerId = resolveSelectedAnswerId(chat.answers, selectedAnswerId);
  const currentIndex = Math.max(
    0,
    chat.answers?.findIndex((answer) => answer?.id === displayedAnswerId) ?? 0
  );
  const currentAnswer = chat.answers?.[currentIndex];
  const selectedPromptVariant = currentAnswer?.prompt_variant || chat.variants?.find(
    (variant) => variant.prompt_id === currentAnswer?.prompt_id
  );
  const selectedPromptContent = selectedPromptVariant?.content ?? chat.content;
  const selectedPromptTimestamp = selectedPromptVariant?.timestamp ?? chat.timestamp;
  const selectedPromptAttachments = selectedPromptVariant?.attachments ?? chat.attachments;
  const selectedPromptActor = selectedPromptVariant?.user_details || chat.user_details;
  const currentVisibleIndex = visibleAnswers.findIndex(
    (answer) => answer.id === currentAnswer?.id
  );

  const {
    cleanedText: answerContent,
  } = transformContent(currentAnswer?.content || "");

  // Build optimistic activity from completed Zustand snapshot while database write propagates
  // streamingActivityItems covers both live streaming and the snapshot window;
  // the store holds activityItems in both the active stream and the completed snapshot.

  // Activity source priority:
  //   1. While streaming (isActivelyStreaming) — always use Zustand live items. The database
  //      is ignored even if the optimistic new answer doc returns with activity: [] during
  //      regeneration, which would otherwise blank the panel mid-stream.
  //   2. After streaming — keep the Zustand snapshot until the database contains
  //      every streamed item, then hand off to the persisted timeline.
  //      Keeps the Zustand snapshot visible during the database write propagation window.
  const researchData = selectActivityItems({
    persistedItems: currentAnswer?.activity,
    streamedItems: streamingActivityItems,
    isActivelyStreaming,
    isCompletedSnapshot,
  });

  // Extract sources from the active data source (Zustand live/snapshot or database).
  // transformContent(content) no longer yields sourcesData — sources are now activity items.
  const sourcesData = useMemo(
    () => (researchData || []).filter((item) => item?.type === 'sources').flatMap((item) => item?.data || []),
    [researchData]
  );

  // Merge completed activity items with any currently-running tool calls as synthetic entries.
  // Running tools appear immediately when TOOL_START fires — before their ACTIVITY_ITEM SSE arrives.
  // Once the matching ACTIVITY_ITEM lands it replaces the synthetic entry.
  const mergedResearchData = useMemo(() => {
    return mergeRunningToolActivity(researchData || [], toolCalls);
  }, [researchData, toolCalls]);

  // Clear optimistic snapshot once real database activity has fully arrived.
  // Two conditions before handing off from Zustand to database:
  //   1. activity.length > 0 — skip the empty-array window that appears before
  //      the database write propagates (prevents the blank-then-reappear flicker).
  //      Plain-text responses with no activity items never populate the snapshot
  //      so isCompletedSnapshot will be false and this never fires for them.
  //   2. Every streamed item is present — count alone is insufficient because a
  //      stale same-length database array can contain a different final event.
  useEffect(() => {
    if (
      containsActivitySnapshot(currentAnswer?.activity, streamingActivityItems) &&
      isCompletedSnapshot
    ) {
      clearCompletedSnapshot(chat.id);
    }
  }, [currentAnswer?.activity, streamingActivityItems, isCompletedSnapshot, chat.id, clearCompletedSnapshot]);

  // For the indicator, we show sources
  const allSources = sourcesData || [];
  const hasSources = allSources.length > 0;

  const files = FileStore((state) => state.files);
  const [isEditing, setIsEditing] = useState(false);
  const [editFiles, setEditFiles] = useState([]);
  const [editActiveSkills, setEditActiveSkills] = useState([]);
  const [editWebSearchEnabled, setEditWebSearchEnabled] = useState(true);
  const [editStyleSelection, setEditStyleSelection] = useState({ mode: "profile_default" });
  const [editStyleVersionId, setEditStyleVersionId] = useState(null);
  const [originalContent, setOriginalContent] = useState(chat.content); // For Storing the original prompt
  const editFileInputRef = useRef(null);
  const authUser = UserAuthStore((state) => state.user);
  const authOrgId = UserAuthStore((state) => state.orgId);
  const isAnswerAvailable = Boolean(currentAnswer?.content?.trim());
  const answerStatus = currentAnswer?.status;
  const isAnswerDisplayable = shouldRenderAnswer({
    answerContent: currentAnswer?.content,
    answerStatus,
    isError: currentAnswer?.isError,
  });
  const showMessageActions = shouldShowMessageActions({
    isAnswerDisplayable,
    hasAnswers: Boolean(chat.answers?.length),
    answerStatus,
    isRegenerating: regenerateIndex === index,
    hasSignal: generate_id === chat.id,
    isStuck,
  });
  const showTerminalStatus = shouldShowTerminalStatus({
    answer: currentAnswer,
    isActivelyStreaming,
    isRegenerating: regenerateIndex === index,
    hasSignal: generate_id === chat.id,
  });

  // Persisted terminal state is authoritative when an SSE terminal event is missed.
  const removeSignal = useGenerateApiSignal((state) => state.removeSignal);
  useEffect(() => {
    const statusMatchesActiveAnswer = isStatusForActiveAnswer({
      answerId: currentAnswer?.id,
      activeAnswerId,
    });
    if (statusMatchesActiveAnswer && shouldClearGenerationSignal({
      signalMessageId: generate_id,
      messageId: chat.id,
      answerContent: currentAnswer?.content,
      answerStatus,
    })) {
      removeSignal(chat.id);
    }
    if (statusMatchesActiveAnswer) {
      useStreamingStateStore.getState().reconcilePersistedStatus(chat.id, answerStatus);
    }
  }, [generate_id, activeAnswerId, chat.id, currentAnswer?.id, currentAnswer?.content, answerStatus, removeSignal]);

  const [editOverflow, setEditOverflow] = useState({ top: false, bottom: false });

  const { data: skillsData } = useGetSkills(authOrgId, { enabled: !!authOrgId && isEditing });
  const availableSkills = useMemo(
    () => (skillsData?.skills || []).filter((s) => s.enabled && s.scope === 'all'),
    [skillsData]
  );

  const toggleEditSkill = (skillId) => {
    setEditActiveSkills((prev) =>
      prev.includes(skillId) ? prev.filter((id) => id !== skillId) : [...prev, skillId]
    );
  };

  // Find approvals matching this answer from stable pendingApprovals state
  // Match by answer_id first, fallback to message_id if no visible answers (all awaiting)
  const matchingApprovals = useMemo(() => {
    if (!pendingApprovals) return [];
    const approvals = Object.values(pendingApprovals);
    // Try to match by answer_id first (for visible answers)
    if (currentAnswer?.id) {
      const matches = approvals.filter(a => a.answer_id === currentAnswer.id);
      if (matches.length > 0) return matches;
    }
    // Fallback to message_id if all answers are awaiting approval
    return approvals.filter(a => a.message_id === chat.id);
  }, [pendingApprovals, currentAnswer?.id, chat.id]);
  // Timer for "Agent is thinking" spinner (also covers LLM streaming state — State 2, 2s threshold)
  const thinkingStartedAtRef = useRef(null);
  const [thinkingElapsed, setThinkingElapsed] = useState(0);
  const isThinkingState = hasContent && !hasRunningTool && matchingApprovals.length === 0;
  useEffect(() => {
    if (isThinkingState) {
      if (!thinkingStartedAtRef.current) thinkingStartedAtRef.current = Date.now();
      const interval = setInterval(() => {
        setThinkingElapsed(Math.floor((Date.now() - thinkingStartedAtRef.current) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    } else {
      thinkingStartedAtRef.current = null;
      setThinkingElapsed(0);
    }
  }, [isThinkingState]);
  const [isLongPrompt, setIsLongPrompt] = useState(false);
  const [isExchangeTopControlVisible, setIsExchangeTopControlVisible] = useState(true);
  const [isExchangeTopControlAboveViewport, setIsExchangeTopControlAboveViewport] = useState(false);
  const [isExchangeFooterControlVisible, setIsExchangeFooterControlVisible] = useState(false);
  const [isExchangeInView, setIsExchangeInView] = useState(false);
  const [stickyControlPosition, setStickyControlPosition] = useState(null);
  const exchangeId = chat.id;
  const collapseEligibility = {
    isStreaming: isActivelyStreaming,
    isRegenerating: regenerateIndex === index || generate_id === chat.id,
    hasPendingApproval: matchingApprovals.length > 0,
  };
  const shouldAutoCollapseExchange = shouldCollapseAnswer({
    compactMode: compactAnswers,
    isLatest,
    ...collapseEligibility,
  });
  const canCollapseExchange = canManuallyCollapseAnswer(collapseEligibility);
  const isExchangeExpanded = expandedExchangeIds?.has(exchangeId) ?? false;
  const isExchangeManuallyCollapsed = collapsedExchangeIds?.has(exchangeId) ?? false;
  const isExchangeCollapsed = canCollapseExchange && (
    isExchangeManuallyCollapsed || (shouldAutoCollapseExchange && !isExchangeExpanded)
  );

  useEffect(() => {
    const promptElement = promptRef.current;
    if (!promptElement) return undefined;

    setIsLongPrompt(false);
    const measurePrompt = () => {
      const measurement = measurePromptElement(promptElement);
      if (measurement?.isLong) setIsLongPrompt(true);
    };
    const frame = requestAnimationFrame(measurePrompt);

    if (typeof ResizeObserver === "undefined") {
      return () => cancelAnimationFrame(frame);
    }
    const observer = new ResizeObserver(measurePrompt);
    observer.observe(promptElement);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [chat.id, chat.content]);

  useEffect(() => {
    const messageElement = messageRef.current;
    const topControlElement = exchangeTopControlRef.current;
    const footerControlElement = exchangeFooterControlRef.current;
    if (isExchangeCollapsed || !canCollapseExchange || !messageElement || !topControlElement) {
      setIsExchangeTopControlVisible(true);
      setIsExchangeTopControlAboveViewport(false);
      setIsExchangeFooterControlVisible(false);
      setIsExchangeInView(false);
      setStickyControlPosition(null);
      return undefined;
    }
    if (typeof IntersectionObserver === "undefined") {
      setIsExchangeTopControlVisible(true);
      setIsExchangeInView(true);
      return undefined;
    }

    const scrollContainer = messageElement.closest("#spark-scroll-container");
    const updateControlState = () => {
      const scrollBounds = scrollContainer?.getBoundingClientRect();
      const messageBounds = messageElement.getBoundingClientRect();
      if (!scrollBounds) return;
      const topControlBounds = topControlElement.getBoundingClientRect();
      const footerControlBounds = footerControlElement?.getBoundingClientRect();
      setIsExchangeTopControlVisible(
        topControlBounds.bottom > scrollBounds.top && topControlBounds.top < scrollBounds.bottom
      );
      setIsExchangeTopControlAboveViewport(topControlBounds.bottom <= scrollBounds.top);
      setIsExchangeFooterControlVisible(Boolean(
        footerControlBounds &&
        footerControlBounds.bottom > scrollBounds.top &&
        footerControlBounds.top < scrollBounds.bottom
      ));
      setIsExchangeInView(messageBounds.bottom > scrollBounds.top && messageBounds.top < scrollBounds.bottom);
      setStickyControlPosition({
        top: scrollBounds.top + 8,
        // Reuse the top inline button's own live left position (not
        // messageBounds.left) so the floating control always lands in the
        // exact same column as the top button and the rail line — the top
        // button's wrapper applies a small breakpoint-specific nudge
        // (-left-1 on mobile, left-0 on desktop) that messageBounds.left
        // doesn't account for.
        left: topControlBounds.left,
      });
    };
    updateControlState();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === topControlElement) {
          setIsExchangeTopControlVisible(entry.isIntersecting);
          setIsExchangeTopControlAboveViewport(
            !entry.isIntersecting && entry.boundingClientRect.bottom <= (entry.rootBounds?.top ?? -Infinity)
          );
        }
        if (entry.target === footerControlElement) setIsExchangeFooterControlVisible(entry.isIntersecting);
        if (entry.target === messageElement) setIsExchangeInView(entry.isIntersecting);
      });
    }, { root: scrollContainer });
    observer.observe(messageElement);
    observer.observe(topControlElement);
    if (footerControlElement) observer.observe(footerControlElement);
    scrollContainer?.addEventListener("scroll", updateControlState, { passive: true });
    window.addEventListener("resize", updateControlState);
    return () => {
      observer.disconnect();
      scrollContainer?.removeEventListener("scroll", updateControlState);
      window.removeEventListener("resize", updateControlState);
    };
  }, [canCollapseExchange, isExchangeCollapsed]);

  const setExchangeExpanded = useCallback((expanded) => {
    const messageElement = messageRef.current;
    const scrollContainer = messageElement?.closest("#spark-scroll-container");
    const initialOffset = messageElement && scrollContainer
      ? messageElement.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top
      : 0;
    onExchangeExpandedChange?.(exchangeId, expanded);

    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (!messageElement || !scrollContainer) return;
      const currentOffset = messageElement.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top;
      scrollContainer.scrollTop += currentOffset - (expanded ? initialOffset : 0);
    }));
  }, [exchangeId, onExchangeExpandedChange]);
  const showStickyExchangeControl = !isExchangeCollapsed &&
    canCollapseExchange &&
    isExchangeTopControlAboveViewport &&
    !isExchangeFooterControlVisible &&
    isExchangeInView &&
    Boolean(stickyControlPosition);
  const [isChanged, setIsChanged] = useState(false); //to check if the answer is modified or not
  const [originalModelId, setOriginalModelId] = useState(null); // model at edit-open time
  const [editModel, setEditModel] = useState(null);
  // Send button visible if text changed, file attached, or model swapped
  const canSave = isChanged || editFiles.length > 0 || (editModel?.id ?? null) !== originalModelId;

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "10px";
      const maxHeight = window.innerHeight * 0.4;
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${Math.max(24, newHeight)}px`;
    }
  };

  const updateEditOverflow = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const hasOverflow = textarea.scrollHeight > textarea.clientHeight + 1;
    if (!hasOverflow) {
      setEditOverflow({ top: false, bottom: false });
      return;
    }

    const top = textarea.scrollTop > 0;
    const bottom = textarea.scrollTop + textarea.clientHeight < textarea.scrollHeight - 1;
    setEditOverflow({ top, bottom });
  };

  useEffect(() => {
    if (!isEditing) return;
    requestAnimationFrame(() => {
      adjustTextareaHeight();
      updateEditOverflow();
    });
  }, [editedContent, isEditing]);

  // Active generation and recovery always follow the answer being written.
  // Otherwise preserve a valid explicit selection across refetches.
  useEffect(() => {
    const answers = chat?.answers || [];
    if (
      regenerateIndex === index ||
      generate_id === chat.id ||
      isEmptyAwaitingRecovery
    ) {
      followLatestAnswer(chat.id, answers);
    } else {
      reconcileSelectedAnswer(chat.id, answers);
    }
  }, [
    chat.id,
    chat?.answers,
    followLatestAnswer,
    generate_id,
    index,
    isEmptyAwaitingRecovery,
    reconcileSelectedAnswer,
    regenerateIndex,
  ]);

  useEffect(() => {
    const chatArea = document.getElementById("chat-messages");
    if (chatArea && messageRef.current) {
      const isAtBottom =
        chatArea.scrollHeight - chatArea.scrollTop === chatArea.clientHeight;
      if (isAtBottom) {
        messageRef.current.style.marginBottom = "8px";
      } else {
        messageRef.current.style.marginBottom = "0px";
      }
    }
  }, [chat]);

  const handleRegenerate = async (
    chatId,
    modelSelected,
    styleSelection,
    styleVersionId
  ) => {
    // Guard mirrors editFunctionality's generateStatus check. Without it, clicking
    // Regenerate/Retry on a past message while a DIFFERENT message is still
    // generating in this chat silently aborts that in-flight generation — the
    // signal/AbortController in useGenerateApiSignal and useMessageLoading's
    // generateStatus/regenerateIndex are all keyed per-chat, not per-message, and
    // generateMessageApi() unconditionally aborts "any existing request for this
    // chat" before starting the new one. The aborted message is left showing an
    // empty skeleton with no explanation (AbortError is treated as already-handled,
    // so no inline error or toast is ever shown for it).
    if (generateStatus) return;
    // regenFunc (-> regenerate() in api/regenerate.js) always resolves — it
    // handles its own errors internally (toast/inline error message) and
    // never rethrows — so no try/catch is needed here.
    setGenerateStatus(true);
    await regenFunc(chatId, index, modelSelected, styleSelection, styleVersionId);
    setGenerateStatus(false);
  };

  const handleNext = () => {
    const visibleIndex = visibleAnswers.findIndex((answer) => answer.id === displayedAnswerId);
    if (visibleIndex >= 0 && visibleIndex < visibleAnswers.length - 1) {
      setSelectedAnswer(chat.id, visibleAnswers[visibleIndex + 1].id);
    }
  };

  const handlePrevious = () => {
    const visibleIndex = visibleAnswers.findIndex((answer) => answer.id === displayedAnswerId);
    if (visibleIndex > 0) {
      setSelectedAnswer(chat.id, visibleAnswers[visibleIndex - 1].id);
    }
  };

  // for redirecting the user to the end of the specific answer when the user clicks on the next or previous button.
  useEffect(() => {
    if (messageRef.current) {
      messageRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [currentIndex]);




  const handleEdit = () => {
    setIsEditing(true);
    setOriginalContent(selectedPromptContent);
    setEditedContent(selectedPromptContent);
    setIsChanged(false);
    // active_skills is written on the answer document, not the prompt document
    const answerSkills = chat.answers?.[currentIndex]?.active_skills;
    setEditActiveSkills(Array.isArray(answerSkills) && answerSkills.length > 0
      ? answerSkills
      : (PromptStore.getState().activeSkills || []));
    setEditWebSearchEnabled(PromptStore.getState().webSearchEnabled ?? true);
    // Snapshot the message's own model so we detect only user-driven swaps
    setOriginalModelId(chat.answers?.[currentIndex]?.model_id ?? null);
    const answer = chat.answers?.[currentIndex];
    setEditStyleSelection(
      answer?.style_id
        ? { mode: "style", style_id: answer.style_id }
        : { mode: answer?.style_selection_mode === "none" ? "none" : "profile_default" }
    );
    setEditStyleVersionId(answer?.style_version_id || null);

    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(
          textareaRef.current.value.length,
          textareaRef.current.value.length
        );
        textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
        adjustTextareaHeight();
      }
    }, 0);
  };

  const handleSave = () => {
    const trimmedContent = editedContent.trim();
    if (!canSave) {
      setIsEditing(false);
      setEditFiles([]);
      return;
    }

    if (!trimmedContent) {
      setEditedContent(originalContent);
    } else {
      PromptStore.getState().setActiveSkills(editActiveSkills);
      editFunctionality(
        index,
        trimmedContent,
        {
          ...chat,
          content: selectedPromptContent,
          asset_ids: selectedPromptVariant?.asset_ids ?? chat.asset_ids,
          attachments: selectedPromptAttachments,
          source_prompt_id: selectedPromptVariant?.prompt_id || currentAnswer?.prompt_id || chat.id,
          source_answer_id: currentAnswer?.id,
        },
        editStyleSelection,
        editStyleSelection.mode === "style" ? editStyleVersionId : null,
        {
          model: editModel,
          webSearchEnabled: editWebSearchEnabled,
          isOverride: Boolean(editModel?.id && editModel.id !== originalModelId),
        }
      );
      PromptStore.getState().setActiveSkills([]);
    }

    setIsEditing(false);
    setIsChanged(false);
    setEditFiles([]);
    setEditActiveSkills([]);
  };

  const handleCancel = useCallback(() => {
    setEditedContent(originalContent);
    setIsEditing(false);
    setIsChanged(false);
    setOriginalModelId(null);
    setEditModel(null);
    setEditFiles([]);
    setEditActiveSkills([]);
  }, [originalContent, setEditedContent, setIsEditing, setIsChanged]);

  // Detect click outside textarea and cancel editing
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (messageRef.current && !messageRef.current.contains(event.target)) {
        if (event.target.closest(".save-button")) return;
        if (event.target.closest("[data-edit-toolbar-portal]")) return;
        handleCancel();
      }
    };

    if (isEditing) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing, handleCancel]);

  return (
    <div
      className="group relative w-full pb-4 last:pb-0 pl-10 sm:pl-12"
      ref={messageRef}
      id={`msg-${chat.id}`}
      key={chat?.content?.length}
    >
      {canCollapseExchange && (
        <>
          <div className="pointer-events-none absolute left-[17px] sm:left-[21px] top-11 bottom-6 w-px bg-fill-hex-d8dde3 dark:bg-fill-gray-600" aria-hidden="true" />
          <div className="absolute -left-1 sm:left-0 top-1 z-20">
            <ExchangeToggleButton
              ref={exchangeTopControlRef}
              expanded={!isExchangeCollapsed}
              onClick={() => setExchangeExpanded(isExchangeCollapsed)}
              hiddenFromAccessibility={!isExchangeCollapsed && !isExchangeTopControlVisible}
            />
          </div>
        </>
      )}
      {showStickyExchangeControl && (
        <div
          data-exchange-sticky-control
          className="fixed z-30 pointer-events-none"
          style={stickyControlPosition}
        >
          <ExchangeToggleButton
            expanded
            onClick={() => setExchangeExpanded(false)}
            floating
          />
        </div>
      )}
      <div className="flex justify-start items-center w-full my-2 group">
        {isScheduling ? (
          <SchedulerCard
            theme={theme}
            chat_id={chat_id}
            space_id={space_id}
            message_id={chat.id}
            source_prompt_id={currentAnswer?.prompt_id || selectedPromptVariant?.prompt_id || chat.id}
            source_answer_id={currentAnswer?.id}
            turn_id={chat.turn_id || chat.id}
            prompt={selectedPromptContent || ""}
            onClose={() => setShowScheduler(false)}
          />
        ) : isEditing ? (
          <div className="photon-chat-message photon-chat-message--page photon-chat-message--user editing w-full">
            {chat.attachments?.length > 0 && (
              <div className="flex items-center gap-[10px] px-3 pt-2 flex-wrap">
                {chat.attachments.map((attachment) => (
                  <FileChip key={attachment.asset_id} attachment={attachment} />
                ))}
              </div>
            )}
            {editFiles?.length > 0 && (
              <div className="flex items-center gap-2 px-3 pt-2 flex-wrap scale-[0.82] origin-top-left">
                {editFiles.map((file) => (
                  <FileUploadItem
                    key={file.name}
                    file={file}
                    onRemove={(f) => setEditFiles((prev) => prev.filter((x) => x !== f))}
                    progress={file?.progress || 0}
                  />
                ))}
              </div>
            )}
            <div className="relative">
              <textarea
                autoFocus
                autoComplete="off"
                aria-label="Edit prompt"
                ref={textareaRef}
                value={editedContent}
                onChange={(e) => {
                  setEditedContent(e.target.value);
                  setIsChanged(e.target.value.trim() !== originalContent.trim());
                  adjustTextareaHeight();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSave();
                  } else if (e.key === "Escape") {
                    handleCancel();
                  }
                }}
                className={`text-[13px] py-[10px] px-[14px] w-full rounded-t-2xl outline-none resize-none overflow-y-auto leading-[18px] bg-transparent ${theme === "dark" ? "text-ink-white caret-ink-white" : "text-ink-black caret-ink-black"}`}
                onScroll={updateEditOverflow}
              />
              {editOverflow.top && (
                <div
                  className="pointer-events-none absolute inset-x-0 top-0 h-3 rounded-t-2xl backdrop-blur-[1px]"
                  style={{
                    background: "linear-gradient(to bottom, color-mix(in srgb, var(--photon-chat-surface) 16%, transparent), color-mix(in srgb, var(--photon-chat-surface) 4%, transparent), transparent)",
                  }}
                />
              )}
              {editOverflow.bottom && (
                <div
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-3 rounded-b-[inherit] backdrop-blur-[1px]"
                  style={{
                    background: "linear-gradient(to top, color-mix(in srgb, var(--photon-chat-surface) 16%, transparent), color-mix(in srgb, var(--photon-chat-surface) 4%, transparent), transparent)",
                  }}
                />
              )}
            </div>
            {/* Compact toolbar */}
            <div className="flex items-center justify-between gap-1 px-2 pb-2 pt-1">
              <div className="flex items-center gap-1 scale-[0.82] origin-left">
                <AttachDropdown
                  fileInputRef={editFileInputRef}
                  filesLength={editFiles.length}
                />
                <PromptStyleSelector
                  value={editStyleSelection}
                  onChange={(selection) => {
                    setEditStyleSelection(selection);
                    setEditStyleVersionId(null);
                    setIsChanged(true);
                  }}
                />
                <PromptSkillSelector
                  skills={availableSkills}
                  selectedSkillIds={editActiveSkills}
                  onToggle={toggleEditSkill}
                />
                <PromptModelSelector
                  compact
                  initialModelId={chat.answers?.[currentIndex]?.model_id || null}
                  onModelSelect={setEditModel}
                />
              </div>
              <div className="flex items-center gap-1.5">
                <PromptDeviceSelector chatId={chat_id} />
                <WebSearchToggle
                  compact
                  enabled={editWebSearchEnabled}
                  onToggle={(next) => {
                    setEditWebSearchEnabled(next);
                    setIsChanged(true);
                  }}
                />
                <MemoryChip />
                <button
                  type="button"
                  aria-label="Cancel prompt edit"
                  className="dark:bg-fill-icon-hover bg-fill-white border border-edge-border dark:border-edge-muted-control dark:bg-opacity-25 flex items-center justify-center rounded-full w-7 h-7"
                  onClick={handleCancel}
                >
                  {theme === "dark" ? <WhiteCross className="w-3 h-3" /> : <BlackCross className="w-3 h-3" />}
                </button>
                {canSave && (
                  <button
                    type="button"
                    aria-label="Save prompt edit"
                    className="photon-primary-action bg-button-primary text-button-on-primary rounded-full w-7 h-7 flex items-center justify-center transition-colors"
                    onMouseDown={handleSave}
                  >
                    <ArrowUp className="rotate-180 w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
            <input
              type="file"
              ref={editFileInputRef}
              className="hidden"
              multiple
              accept={ALLOWED_FILE_ACCEPT_STRING}
              onChange={(e) => {
                handleFileUpload(
                  e,
                  chat_id,
                  editFileInputRef,
                  setEditFiles,
                  editFiles,
                  authUser,
                  authOrgId,
                  0,
                  false,
                  space,
                  null
                );
                if (editFileInputRef.current) editFileInputRef.current.value = "";
              }}
            />
          </div>
        ) : (
          <div className="w-full min-w-0">
            <div
              className={`photon-chat-message photon-chat-message--page photon-chat-message--user message relative opacity-100 px-2 sm:px-4 py-1 break-words overflow-x-hidden ${generateStatus ? "cursor-default" : "cursor-text"}`}
              onClick={(event) => {
                if (!generateStatus && !event.target.closest("button, a, input, textarea, select")) {
                  handleEdit();
                }
              }}
              onKeyDown={(event) => {
                if (!generateStatus && event.key === "Enter" && event.target === event.currentTarget) {
                  event.preventDefault();
                  handleEdit();
                }
              }}
              role="group"
              aria-label="Prompt. Press Enter to edit."
              tabIndex={generateStatus ? -1 : 0}
            >
              <div
                ref={promptRef}
                className={`whitespace-pre-wrap break-words my-1.5 pr-10 leading-6 sm:leading-7 font-inter text-[15px] ${isLongPrompt && isExchangeCollapsed ? "max-h-[6.75rem] overflow-hidden line-clamp-4" : ""}`}
              >
                {renderTextWithTags(selectedPromptContent, validTags, triggers)}
              </div>
            {/* Sources indicator - only show if we have sources */}
            {sourcesData && sourcesData.length > 0 && (
              <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-edge-gray-200 dark:border-edge-gray-600">
                <svg className="w-3.5 h-3.5 text-ink-gray-400 dark:text-ink-gray-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5z" />
                  <path d="M7.414 15.414a2 2 0 01-2.828-2.828l3-3a2 2 0 012.828 0 1 1 0 001.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5z" />
                </svg>
                <div className="flex items-center gap-1">
                  {hasSources ? (
                    <>
                      <span className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
                        {allSources.length} source{allSources.length !== 1 ? 's' : ''}
                      </span>
                      <span className="text-xs text-ink-gray-400 dark:text-ink-gray-500">•</span>
                      <button
                        onClick={() => {
                          const sourcesElement = document.querySelector(`[data-sources-section="${chat_id}-${index}"]`);
                          if (sourcesElement) {
                            sourcesElement.scrollIntoView({
                              behavior: 'smooth',
                              block: 'start'
                            });
                          }
                        }}
                        className="text-xs text-ink-blue-600 dark:text-ink-blue-400 hover:text-ink-blue-800 dark:hover:text-ink-blue-300 transition-colors duration-200 flex items-center gap-1"
                      >
                        View sources
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            )}
            <button
              className={`absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full border-[0.5px] border-edge-border bg-fill-white shadow-sm transition-opacity duration-200 hover:bg-fill-gray-100 focus:opacity-100 dark:border-transparent dark:bg-fill-selection-surface-dark dark:hover:bg-fill-icon-hover-dark ${isScheduled ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
              onClick={() => setShowScheduler(true)}
              aria-label={isScheduled ? "Edit prompt schedule" : "Schedule this prompt"}
              title={isScheduled ? "Scheduled — click to edit" : "Schedule this prompt"}
            >
              <CalendarClock className={`h-4 w-4 ${isScheduled ? "text-ink-primary dark:text-ink-accent" : "text-ink-ink dark:text-ink-white"}`} />
            </button>
            </div>
          </div>
        )}

      </div>
      
      <PromptMetadata
        timestamp={selectedPromptTimestamp}
        actor={selectedPromptActor}
        showActor={Boolean(space_id)}
      />

      {/* System messages attached to this prompt (e.g., approval cancelled/expired) */}
      {chat.system_messages && chat.system_messages.length > 0 && (
        <div className="w-full my-2">
          {chat.system_messages.map((systemMsg) => (
            <div
              key={systemMsg.id}
              className="photon-chat-message photon-chat-message--page photon-chat-message--assistant message py-1 my-2 w-full break-words overflow-x-hidden font-inter min-w-0"
            >
              <div className="whitespace-pre-wrap">
                {systemMsg.content}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* File attachments — shown immediately after send, before answer arrives */}
      {!isEditing && selectedPromptAttachments?.length > 0 && (
        <div className="flex items-center gap-[10px] my-2 flex-wrap">
          {selectedPromptAttachments.map((attachment) => (
            <FileChip key={attachment.asset_id} attachment={attachment} />
          ))}
        </div>
      )}

      {/* Activity card — shown when there are renderable (non-sources) activity items.
          sources items are already shown as citation chips via sourcesData/YouTubeVideosSection;
          counting them here caused the Activity button to appear but the panel to be empty
          because ShowResearch's FILTER excludes items with no title/description. */}
      {!isExchangeCollapsed && mergedResearchData?.some(item => item.type !== 'sources') ? (
        <div className="-mt-2">
          <ShowResearch
            activityOpen={activityOpen}
            setActivityOpen={setActivityOpen}
            researchData={mergedResearchData}
            chat_id={chat_id}
            message_id={chat.id}
            status={"NO_NEED"}
            answerContent={answerContent}
          />
        </div>
      ) : null}

      {/* only shown when answer has started OR approval is pending */}
      {(isAnswerDisplayable || showMessageActions || matchingApprovals.length > 0 || connectionRequirement) ? (
        <>
          <MermaidSvgRegistryProvider>
          {isExchangeCollapsed ? (
            <div className="message my-2 py-1 w-full min-w-0 overflow-hidden">
              <AnswerPreview
                content={answerContent}
                remarkPlugins={MARKDOWN_REMARK_PLUGINS}
                rehypePlugins={MARKDOWN_REHYPE_PLUGINS}
              />
            </div>
          ) : (
          <div
            key={chat.id || chat.content?.length}
            className="photon-chat-message photon-chat-message--page photon-chat-message--assistant message py-1 my-2 w-full break-words overflow-x-hidden font-inter min-w-0"
          >
            <YouTubeVideosSection sources={sourcesData} />

            {/* Render approval card if a pending approval matches this message */}
            <div>
            {connectionRequirement ? (
              <ProviderConnectionCard
                requirement={connectionRequirement}
                userId={userId}
                orgId={orgId}
                chatId={chat_id}
                spaceId={space?.space_id}
              />
            ) : matchingApprovals.length > 0 ? (
              <div className="flex flex-col gap-3">
                {matchingApprovals.map((approval) => (
                  <ApprovalCard
                    key={approval.approval_id}
                    cardData={approval}
                    onApprove={onApprovalConfirm}
                    onCancel={onApprovalCancel}
                    onExpire={onApprovalExpire}
                  />
                ))}
              </div>
            ) : (
              /* Enhanced Message Content with Media Support */
              currentAnswer?.media_attachments && currentAnswer.media_attachments.length > 0 ? (
                <EnhancedMessageContent
                  message={{
                    type: 'media',
                    content: answerContent,
                    media_attachments: currentAnswer.media_attachments,
                    metadata: currentAnswer.metadata
                  }}
                />
              ) : currentAnswer?.status === "denied" && !currentAnswer?.error_title ? null : (
                <CodeBlockSaveContext.Provider value={{ answerId: currentAnswer?.id, chatOwnerId: space?.owner_id || userId, rawContent: currentAnswer?.content || "", isStreaming: isActivelyStreaming }}>
                  <MarkdownRenderer content={answerContent} />
                </CodeBlockSaveContext.Provider>
              )
            )}
            </div>

            <CitationsPreview sources={sourcesData} messageId={`${chat_id}-${index}`} />

            {/* Stopped / partial / interrupted / failed / denied / error status indicator */}
            {showTerminalStatus && (
              <div className="flex items-center gap-2 mt-2 mb-1 text-xs" role="status" aria-live="polite">
                {currentAnswer.status === 'denied' ? (
                  <span className="flex items-center gap-1.5 text-ink-hex-d97706 dark:text-ink-amber-400">
                    {currentAnswer.error_code?.startsWith('quota')
                      ? <MdHourglassEmpty className="w-3.5 h-3.5 flex-shrink-0" />
                      : <MdBlock className="w-3.5 h-3.5 flex-shrink-0" />}
                    {currentAnswer.error_title || DENIAL_ERROR_MESSAGES[currentAnswer.error_code] || "Access denied. Contact your admin."}
                  </span>
                ) : currentAnswer.status === 'expired' ? (
                  <span className="text-ink-hex-d97706 dark:text-ink-amber-400">{currentAnswer.error_title || "Approval expired"}</span>
                ) : currentAnswer.isError ? (
                  <span className="text-ink-red-500 dark:text-ink-red-400">{currentAnswer.error_title || "Something went wrong"}</span>
                ) : currentAnswer.status === 'stopped' ? (
                  <span className="text-ink-amber-500 dark:text-ink-amber-400">
                    {isAnswerAvailable ? "Generation was stopped" : "Generation stopped before a response was produced."}
                  </span>
                ) : currentAnswer.status === 'interrupted' ? (
                  <span className="text-ink-amber-500 dark:text-ink-amber-400">Generation was interrupted</span>
                ) : currentAnswer.status === 'failed' ? (
                  <span className="text-ink-red-500 dark:text-ink-red-400">{currentAnswer.error_title || "Generation failed"}</span>
                ) : (
                  <span className="text-ink-amber-500 dark:text-ink-amber-400">Response may be incomplete</span>
                )}
                {canRetryAnswer(currentAnswer) && regenerateIndex !== index && generate_id !== chat.id && (
                  <button
                    onClick={() => handleRegenerate(
                      chat.id,
                      currentAnswer?.model_id,
                      currentAnswer?.style_id
                        ? { mode: "style", style_id: currentAnswer.style_id }
                        : { mode: currentAnswer?.style_selection_mode === "none" ? "none" : "profile_default" },
                      currentAnswer?.style_version_id || null
                    )}
                    className="px-2 py-0.5 rounded text-xs font-medium bg-fill-blue-100 text-ink-blue-700 hover:bg-fill-blue-200 dark:bg-fill-blue-900/40 dark:text-ink-blue-300 dark:hover:bg-fill-blue-800/60 transition-colors"
                  >
                    Retry
                  </button>
                )}
              </div>
            )}
            
            {/* Timestamp above action buttons */}
            {currentAnswer?.timestamp && (
              <div className="flex justify-end w-full pr-4 mt-3 mb-6">
                <span className="text-xs text-ink-gray-400 dark:text-ink-gray-500">
                  {formatTimestamp(currentAnswer.timestamp)}
                </span>
              </div>
            )}
            
            {showMessageActions ? (
              <MessageFooter
                leadingAction={canCollapseExchange && !isExchangeCollapsed ? (
                  <div className="-ml-11 sm:-ml-12 mr-1 flex items-center">
                    <ExchangeToggleButton
                      ref={exchangeFooterControlRef}
                      expanded
                      onClick={() => setExchangeExpanded(false)}
                      hiddenFromAccessibility={showStickyExchangeControl}
                    />
                  </div>
                ) : null}
                regenFunc={handleRegenerate}
                chat={chat}
                handlePrevious={handlePrevious}
                handleNext={handleNext}
                disablePrevious={currentVisibleIndex <= 0}
                disableNext={currentVisibleIndex === visibleAnswers.length - 1}
                currentIndex={currentIndex}
                totalAnswers={chat.answers?.length || 0}
                content={answerContent}
                files={files}
                index={index}
                regenerateIndex={regenerateIndex}
                setRegenerateIndex={setRegenerateIndex}
                modelSelected={chat.answers?.[currentIndex]?.model_id}
                hideRegenerate={!canRetryAnswer(currentAnswer)}
                deleteMessage={deleteMessage}
                isLatest={isLatest}
              />
            ) : null}
          </div>
          )}
          </MermaidSvgRegistryProvider>
        </>
      ) : null}

      <GenerationStatusIndicator
        visible={matchingApprovals.length === 0 && (isStopping || regenerateIndex === index || generate_id === chat.id || (isEmptyAwaitingRecovery && !stuckDismissed))}
        isStuck={isStuck}
        hasContent={hasContent}
        hasRunningTool={hasRunningTool}
        statusText={isStopping ? "Stopping" : statusText}
        generateStatus={generateStatus}
        skeletonElapsed={skeletonElapsed}
        thinkingElapsed={thinkingElapsed}
        theme={theme}
        canStop={canStop && !isStopping}
        onStop={async () => {
          const answerId = _recoveryAns?.id;
          if (!answerId) return;
          const stoppingIds = new Set([chat.id, answerId]);
          stoppingIds.forEach((id) => useStreamingStateStore.getState().markStopping(id));
          setStuckDismissed(true);
          try {
            await stopGenerationAndReconcile({
              chatId: chat_id,
              spaceId: space?.space_id,
              answerId,
              generationId: answerId,
              onTerminal: () => {
                stoppingIds.forEach((id) => useStreamingStateStore.getState().clearStopping(id));
                queryClient.invalidateQueries({
                  queryKey: [QUERYKEYS.CHAT_MESSAGES, chat_id, space?.space_id],
                });
              },
            });
          } catch {
            stoppingIds.forEach((id) => useStreamingStateStore.getState().clearStopping(id));
            setStuckDismissed(false);
          }
        }}
        onDismiss={() => setStuckDismissed(true)}
      />

        {/* Keep recovery actions available after the stuck warning is dismissed. */}
      {stuckDismissed && !showMessageActions ? (
        <MessageFooter
          regenFunc={handleRegenerate}
          chat={chat}
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          disablePrevious={currentVisibleIndex <= 0}
          disableNext={currentVisibleIndex === visibleAnswers.length - 1}
          currentIndex={currentIndex}
          totalAnswers={chat.answers?.length || 0}
          content=""
          files={files}
          index={index}
          regenerateIndex={regenerateIndex}
          setRegenerateIndex={setRegenerateIndex}
          modelSelected={chat.answers?.[currentIndex]?.model_id}
          hideRegenerate={!canRetryAnswer(currentAnswer)}
          deleteMessage={deleteMessage}
          isLatest={isLatest}
        />
      ) : null}
    </div>
  );
});

export default OneMessage;
